import { normalizeWebsiteUrl } from '@mts/shared/socialLinks';
import React from 'react';
import BasicAboutUsCard from './BasicAboutUsCard';
import BasicBusinessHoursCard from './BasicBusinessHoursCard';
import BasicBusinessMapCard from './BasicBusinessMapCard';
import BasicContactCard from './BasicContactCard';
import BasicDealsCard from './BasicDealsCard';
import EditableListingInfoRegion from './EditableListingInfoRegion';
import BasicHeroCard from './BasicHeroCard';
import BasicPageBody from './BasicPageBody';
import { Phone, MapPin, Tag, Globe, MoreHorizontal } from 'lucide-react';
import BasicPhotosCard from './BasicPhotosCard';
import BasicReviewsCard from './BasicReviewsCard';
import BasicRightSidebar from './BasicRightSidebar';
import BasicSocialLinksCard from './BasicSocialLinksCard';
import BasicSpotlightModulesCard from './BasicSpotlightModulesCard';


function pickFirstListingField(candidates, fallbackValue = '') {
  for (const candidate of candidates) {
    const value = candidate?.value;
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && !value.trim()) continue;
    return {
      value: typeof value === 'string' ? value.trim() : value,
      field: candidate.field,
    };
  }

  return {
    value: fallbackValue,
    field: 'fallback',
  };
}

function resolveListingMediaLibraryPhotos(listing) {
  const photos = Array.isArray(listing?.galleryImages) ? listing.galleryImages : [];

  return photos
    .filter((url) => typeof url === 'string' && url.trim())
    .map((url, index) => ({
      id: `photo-${index + 1}`,
      url: url.trim(),
      caption: `Photo ${index + 1}`,
    }));
}

function resolveListingSocialLinks(listing) {
  if (listing?.socialLinks && typeof listing.socialLinks === 'object' && !Array.isArray(listing.socialLinks)) {
    return {
      value: listing.socialLinks,
      field: 'listing.socialLinks',
    };
  }

  if (listing?.socialLinksJson && typeof listing.socialLinksJson === 'object' && !Array.isArray(listing.socialLinksJson)) {
    return {
      value: listing.socialLinksJson,
      field: 'listing.socialLinksJson',
    };
  }

  return {
    value: {},
    field: 'fallback',
  };
}

function BasicTierSkeleton({
  shareData,
  listing,
  tagsPanel,
  navigationLocked = false,
  previewHotspot = null,
  forceBusinessNameUntint = false,
  canEditListingInfo = false,
  onEditAbout,
  onEditContact,
  onEditCategories,
  onEditSocial,
  onEditName,
  onEditHours,
  onEditPhotos,
  onClaim,
  deals = [],
  dealsLoading = false,
  dealsError = '',
}) {
    // Featured tier sticky menu config
    const featuredNavItems = [
      { key: 'call', label: 'Call', icon: Phone },
      { key: 'directions', label: 'Directions', icon: MapPin },
      { key: 'offers', label: 'Offers', icon: Tag },
      { key: 'website', label: 'Website', icon: Globe },
      { key: 'more', label: 'More', icon: MoreHorizontal },
    ];
  const tierKey = String(listing?.tierInfo?.key || listing?.tier || 'basic').toLowerCase();
  const isBasicTier = tierKey === 'basic';
  const isFeaturedTier = tierKey === 'featured';
  const isSpotlightTier = tierKey === 'spotlight';

  const nameField = pickFirstListingField([
    { field: 'listing.name', value: listing?.name },
  ], 'Listing');
  const assignedPrimaryCategory = Array.isArray(listing?.listingCategories)
    ? listing.listingCategories.map((entry) => entry?.tenantCategory).find((item) => item && !item.parentId)
    : null;
  const categoryField = pickFirstListingField([
    { field: 'listing.primaryCategory', value: listing?.primaryCategory },
    { field: 'listing.category', value: listing?.category },
    { field: 'listing.categoryGroup', value: listing?.categoryGroup },
    { field: 'listing.listingCategories.primary.tenantCategory.name', value: assignedPrimaryCategory?.name },
    { field: 'listing.listingCategories[0].coreCategory.name', value: listing?.listingCategories?.[0]?.coreCategory?.name },
    { field: 'listing.listingCategories[0].category.name', value: listing?.listingCategories?.[0]?.category?.name },
    { field: 'listing.listingCategories[0].tenantCategory.name', value: listing?.listingCategories?.[0]?.tenantCategory?.name },
    { field: 'listing.listingCategories[0].tenantCategory.coreCategory.name', value: listing?.listingCategories?.[0]?.tenantCategory?.coreCategory?.name },
  ], 'Local Business');
  const shortDescriptionField = pickFirstListingField([
    { field: 'listing.shortDescription', value: listing?.shortDescription },
  ], '');
  const descriptionField = pickFirstListingField([
    { field: 'listing.description', value: listing?.description },
  ], '');
  const phoneField = pickFirstListingField([
    { field: 'listing.phone', value: listing?.phone },
    { field: 'listing.phoneNumber', value: listing?.phoneNumber },
  ], '');
  const addressField = pickFirstListingField([
    { field: 'listing.address', value: listing?.address },
    { field: 'listing.addressLine1', value: listing?.addressLine1 },
  ], '');
  const areaField = pickFirstListingField([
    { field: 'listing.shortAddress', value: listing?.shortAddress },
    { field: 'listing.shortAddressDescription', value: listing?.shortAddressDescription },
    { field: 'listing.addressShort', value: listing?.addressShort },
    { field: 'listing.city', value: listing?.city },
    { field: 'listing.details.shortAddress', value: listing?.details?.shortAddress },
    { field: 'listing.details.shortAddressDescription', value: listing?.details?.shortAddressDescription },
    { field: 'listing.details.addressShort', value: listing?.details?.addressShort },
    { field: 'listing.details.city', value: listing?.details?.city },
  ], '');
  const ratingField = pickFirstListingField([
    { field: 'listing.rating', value: listing?.rating },
  ], 0);
  const reviewsField = pickFirstListingField([
    { field: 'listing.reviewCount', value: listing?.reviewCount },
  ], 0);
  const logoField = pickFirstListingField([
    { field: 'listing.imageUrl', value: listing?.imageUrl },
    { field: 'listing.logoUrl', value: listing?.logoUrl },
    { field: 'listing.logo', value: listing?.logo },
    { field: 'listing.details.imageUrl', value: listing?.details?.imageUrl },
    { field: 'listing.details.logoUrl', value: listing?.details?.logoUrl },
    { field: 'listing.details.logo', value: listing?.details?.logo },
  ], '');
  const headerPrimaryImageField = pickFirstListingField([
    { field: 'listing.imageUrl', value: listing?.imageUrl },
    { field: 'listing.details.imageUrl', value: listing?.details?.imageUrl },
    { field: 'listing.galleryImages[0]', value: listing?.galleryImages?.[0] },
  ], '');
  const websiteField = pickFirstListingField([
    { field: 'listing.website', value: listing?.website },
    { field: 'listing.websiteUrl', value: listing?.websiteUrl },
  ], '');
  const emailField = pickFirstListingField([
    { field: 'listing.email', value: listing?.email },
    { field: 'listing.contactEmail', value: listing?.contactEmail },
    { field: 'listing.details.email', value: listing?.details?.email },
    { field: 'listing.details.contactEmail', value: listing?.details?.contactEmail },
  ], '');
  const businessHoursField = pickFirstListingField([
    { field: 'listing.hours.weekly', value: listing?.hours?.weekly },
    { field: 'listing.businessHours', value: listing?.businessHours },
  ], []);
  const latField = pickFirstListingField([
    { field: 'listing.lat', value: listing?.lat },
  ], null);
  const lngField = pickFirstListingField([
    { field: 'listing.lng', value: listing?.lng },
  ], null);

  const name = String(nameField.value || 'Listing');
  const category = String(categoryField.value || 'Local Business');
  const shortDescription = String(shortDescriptionField.value || '');
  const description = String(descriptionField.value || '');
  const phone = String(phoneField.value || '');
  const address = String(addressField.value || '');
  const area = String(areaField.value || '');
  const rating = Number(ratingField.value || 0);
  const reviews = Number(reviewsField.value || 0);
  const logoUrl = String(logoField.value || '');
  const headerPrimaryImageUrl = String(headerPrimaryImageField.value || '');
  const websiteRaw = String(websiteField.value || '');
  const website = normalizeWebsiteUrl(websiteRaw);
  const email = String(emailField.value || '');
  const directionsQuery = address || name;
  const directionsField = address ? addressField.field : nameField.field;
  const mediaLibraryPhotos = resolveListingMediaLibraryPhotos(listing);
  const socialLinksField = resolveListingSocialLinks(listing);
  const businessHoursRows = Array.isArray(businessHoursField.value) ? businessHoursField.value : [];
  const lat = latField.value === null ? null : Number(latField.value);
  const lng = lngField.value === null ? null : Number(lngField.value);
  const mapField = Number.isFinite(lat) && Number.isFinite(lng)
    ? `${latField.field}, ${lngField.field}`
    : addressField.field;

  return (
    <article id="SingleListingView" className="space-y-4 text-black">
      <BasicPageBody>
        <div id="SingleListingHeroRegion" className="mb-8 lg:mb-10">
          <EditableListingInfoRegion id="SingleListingHeroEditableRegion" canEdit={canEditListingInfo}>
            <BasicHeroCard
              shareData={shareData}
              name={name}
              category={category}
              area={area}
              rating={rating}
              reviews={reviews}
              description={shortDescription}
              logoUrl={logoUrl}
              nameField={nameField.field}
              categoryField={categoryField.field}
              areaField={areaField.field}
              ratingField={ratingField.field}
              reviewsField={reviewsField.field}
              descriptionField={shortDescriptionField.field}
              logoField={logoField.field}
              phone={phone}
              website={website}
              directionsQuery={directionsQuery}
              phoneField={phoneField.field}
              websiteField={websiteField.field}
              directionsField={directionsField}
              navigationLocked={navigationLocked}
              isFeaturedTier={isFeaturedTier}
              isSpotlightTier={isSpotlightTier}
              spotlightBackgroundImageUrl={headerPrimaryImageUrl}
              listingId={listing?.id}
              previewHotspot={previewHotspot}
              forceBusinessNameUntint={forceBusinessNameUntint}
              canEdit={canEditListingInfo}
              onEditName={onEditName}
            />
          </EditableListingInfoRegion>
        </div>

        <div id="SingleListingContentLayout" className="lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-6">
          <div id="SingleListingMainColumn" className="space-y-0">
            <EditableListingInfoRegion id="AboutUsEditableRegion" canEdit={canEditListingInfo}>
              <BasicAboutUsCard
                description={description}
                descriptionField={descriptionField.field}
                listingMeta={listing?.metaJson}
                canEdit={canEditListingInfo}
                onEdit={onEditAbout}
              />
            </EditableListingInfoRegion>

            <EditableListingInfoRegion id="SocialEditableRegion" canEdit={canEditListingInfo}>
              <BasicSocialLinksCard
                socialLinks={socialLinksField.value}
                socialField={socialLinksField.field}
                canEdit={canEditListingInfo}
                onEdit={onEditSocial}
              />
            </EditableListingInfoRegion>

            <EditableListingInfoRegion id="BusinessHoursEditableRegion" canEdit={canEditListingInfo}>
              <BasicBusinessHoursCard
                hoursRows={businessHoursRows}
                hoursField={businessHoursField.field}
                canEdit={canEditListingInfo}
                onEdit={onEditHours}
              />
            </EditableListingInfoRegion>

            {((listing?.locationType || 'PHYSICAL') === 'PHYSICAL' && (listing?.addressVisibility || 'PUBLIC') === 'PUBLIC') ? <EditableListingInfoRegion id="BusinessMapEditableRegion" canEdit={canEditListingInfo}>
              <BasicBusinessMapCard
                lat={lat}
                lng={lng}
                address={address}
                name={name}
                mapField={mapField}
                isBasicTier={isBasicTier}
              />
            </EditableListingInfoRegion> : null}

            <EditableListingInfoRegion id="PhotosEditableRegion" canEdit={canEditListingInfo}>
              <BasicPhotosCard
                photos={mediaLibraryPhotos}
                isBasicTier={isBasicTier}
                canEdit={canEditListingInfo}
                onEdit={onEditPhotos}
              />
            </EditableListingInfoRegion>
            <EditableListingInfoRegion id="DealsEditableRegion" canEdit={canEditListingInfo}>
              <BasicDealsCard deals={deals} loading={dealsLoading} error={dealsError} />
            </EditableListingInfoRegion>

            {isSpotlightTier ? (
              <EditableListingInfoRegion id="SpotlightModulesEditableRegion" canEdit={canEditListingInfo}>
                <BasicSpotlightModulesCard listing={listing} />
              </EditableListingInfoRegion>
            ) : null}

            <EditableListingInfoRegion id="ReviewsEditableRegion" canEdit={canEditListingInfo}>
              <BasicReviewsCard
                listingSlug={listing?.slug}
                categorySlug={listing?.listingCategories?.[0]?.tenantCategory?.slug}
              />
            </EditableListingInfoRegion>

            <EditableListingInfoRegion id="ContactEditableRegion" canEdit={canEditListingInfo}>
              <BasicContactCard
                phone={phone}
                website={website}
                email={email}
                phoneField={phoneField.field}
                websiteField={websiteField.field}
                emailField={emailField.field}
                address={address}
                addressField={addressField.field}
                canEdit={canEditListingInfo}
                onEdit={onEditContact}
              />
            </EditableListingInfoRegion>
          </div>

          <BasicRightSidebar
            tagsPanel={tagsPanel}
            category={category}
            area={area}
            listing={listing}
            canEditListingInfo={canEditListingInfo}
            onEditCategories={onEditCategories}
            onClaim={onClaim}
          />
        </div>
      </BasicPageBody>

      {/* Sticky bottom menu for featured tier only */}
      {isFeaturedTier && (
        <nav
          id="FeaturedMobileStickyNav"
          aria-label="Mobile navigation"
          className="fixed bottom-0 left-0 right-0 z-[1020] flex md:hidden"
          style={{
            backgroundColor: 'var(--primary-soft)',
            borderTop: '1px solid var(--primary)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          {featuredNavItems.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              aria-label={label}
              className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2 transition-opacity active:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-inset"
              style={{
                color: 'var(--primary)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span className="flex items-center justify-center rounded-full px-3 py-0.5">
                <Icon size={20} strokeWidth={1.7} style={{ color: 'var(--primary)' }} aria-hidden="true" />
              </span>
              <span
                className="text-[10px] font-semibold leading-none tracking-wide"
                style={{ color: 'var(--primary)', fontFamily: 'var(--font-body, inherit)' }}
              >
                {label}
              </span>
            </button>
          ))}
        </nav>
      )}
    </article>
  );
}

export default BasicTierSkeleton;
